import { useEffect, useRef, useState } from "react";
import type { EmojiStampKind, StampCanvasObject, StampKind } from "@refinement-canvas/domain";
import { resolveObjectCursor, useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { useCanvasViewersStore } from "../store/canvas-viewers-store";
import { isSnapshotAvatarImageUrl } from "../../../auth/avatar-image-url";

interface Props {
  readonly object: StampCanvasObject;
}

// スタンプ（絵文字風アイコン / アバター画像）オブジェクト。
// - 絵文字種別は 1 文字の絵文字グリフとして描画する
// - "avatar" 種別は配置時にスナップショットした利用者アバター画像を描画する
// - ペンツール中はクリックが描画優先のため pointer-events を切る
// - 他の図形と同様に選択中はアクセントカラーの枠を出す
export function StampObject({ object }: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));
  const readOnly = useCanvasStore((s) => s.readOnly);

  // 作成者（スタンプを置いた人）の表示名。永続スナップショット（createdByName）を優先し、
  // 旧データで未設定の場合は現在接続中の参加者プレゼンスからベストエフォートで解決する
  // （作成者がオフラインだと解決できず、その場合はバッジを出さない）。
  const presenceCreatorName = useCanvasViewersStore(
    (s) => s.viewers[object.createdBy]?.displayName
  );
  const creatorName = object.createdByName ?? presenceCreatorName;

  // アバター画像の表示可否判定。avatar スタンプは「安全なラスタ data:image/ スナップショット」のみ
  // 描画し、サーバ ObjectSchema・配置時の保存条件と最終描画境界を完全一致させる
  // （信頼済み https や data:image/svg+xml 等も描画境界では通さない）。
  const [imgErrored, setImgErrored] = useState(false);
  const avatarSrc = object.avatarSrc;
  useEffect(() => setImgErrored(false), [avatarSrc]);

  useObjectDrag({
    objectId: object.id,
    elementRef: containerRef,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  // 種別に応じた中身（アバター画像 / 絵文字グリフ）。
  let inner: JSX.Element;
  if (object.stampKind === "avatar") {
    const showAvatarImage = !imgErrored && isSnapshotAvatarImageUrl(avatarSrc);
    inner = showAvatarImage ? (
      <img
        src={avatarSrc}
        alt=""
        referrerPolicy="no-referrer"
        // ネイティブの画像ドラッグ（ゴースト生成）を抑止し、オブジェクトのドラッグ移動を妨げない。
        draggable={false}
        onError={() => setImgErrored(true)}
        // コンテナいっぱいに丸く表示する。% 指定なのでリサイズ中も即追従する。
        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
      />
    ) : (
      // アバター未設定・URL 不正・読込失敗時のフォールバック（人物アイコン絵文字）。
      <StampGlyph glyph="👤" />
    );
  } else {
    inner = <StampGlyph glyph={STAMP_KIND_GLYPH[object.stampKind] ?? UNKNOWN_STAMP_GLYPH} />;
  }

  return (
    <div
      ref={containerRef}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="stamp"
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        // 参照専用では動かせないため default。配置ツール・ペン描画の選択中は新規作成（crosshair）であることを示し、移動と誤認させない。
        cursor: resolveObjectCursor({ readOnly, activeTool }),
        // 選択枠は FigJam 風の水色で、ズーム倍率に依らず画面上一定の太さ・オフセットにする。
        outline: isSelected
          ? "calc(2px * var(--canvas-inv-zoom, 1)) solid var(--color-selection)"
          : "none",
        outlineOffset: "calc(2px * var(--canvas-inv-zoom, 1))",
        display: "grid",
        placeItems: "center",
        userSelect: "none",
        // 絵文字は OS のカラー絵文字フォントへフォールバックさせる（SVG text がこの font-family を継承する）。
        fontFamily:
          '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Twemoji Mozilla", "Segoe UI Symbol", sans-serif',
        lineHeight: 1,
      }}
      aria-label={`スタンプ: ${STAMP_KIND_LABEL[object.stampKind] ?? UNKNOWN_STAMP_LABEL}`}
    >
      {inner}
      {isSelected && creatorName ? <StampCreatorBadge name={creatorName} rotation={object.rotation} /> : null}
    </div>
  );
}

/**
 * スタンプの絵文字グリフを、図形の枠内のど真ん中に描画する。
 * DOM の inline テキストはフォントメトリクス（ascent/descent が 1em を超える等）により
 * em ボックス内でインク（見た目の絵）が上下に偏るため、正方形の中央に見えない問題があった。
 * SVG text の dominantBaseline="central"（Canvas2D の textBaseline="middle" と同じメトリクス基準）で
 * 垂直中央へ揃え、PDF 出力・サムネイル（textBaseline="middle"）と見た目を一致させる。
 * width/height を 100% にすることで、リサイズのライブプレビュー（DOM 直書き）にも React 再計算なしで追従する。
 */
function StampGlyph({ glyph }: { readonly glyph: string }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="100%"
      height="100%"
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid meet"
    >
      <text x="50" y="50" textAnchor="middle" dominantBaseline="central" fontSize="85">
        {glyph}
      </text>
    </svg>
  );
}

/**
 * スタンプ選択時に「誰が置いたか」を示す吹き出しバッジ（FigJam 風）。スタンプの上辺中央に、
 * ズーム倍率に依らない一定サイズで表示する（リモートキャレットの名前ラベルと同じ補正パターン）。
 * スタンプが回転している場合は逆回転してバッジの文字を水平に保つ。
 */
function StampCreatorBadge({
  name,
  rotation,
}: {
  readonly name: string;
  readonly rotation?: number | undefined;
}): JSX.Element {
  const counterRotate = rotation ? ` rotate(${-rotation}deg)` : "";
  return (
    <div
      aria-label={`スタンプ作成者: ${name}`}
      style={{
        position: "absolute",
        left: "50%",
        bottom: "100%",
        // ズーム非依存サイズ + 水平中央寄せ + （回転スタンプでは）逆回転で文字を水平に保つ。
        transform: `translateX(-50%) scale(var(--canvas-inv-zoom, 1))${counterRotate}`,
        transformOrigin: "center bottom",
        marginBottom: "calc(6px * var(--canvas-inv-zoom, 1))",
        padding: "2px 8px",
        background: "var(--color-selection)",
        color: "#ffffff",
        fontSize: "11px",
        fontWeight: 600,
        borderRadius: "10px",
        whiteSpace: "nowrap",
        boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
        pointerEvents: "none",
        userSelect: "none",
        // 絵文字用フォントの継承を打ち消し、名前は通常フォントで描画する。
        fontFamily: "sans-serif",
      }}
    >
      {name}
    </div>
  );
}

/** 絵文字スタンプ種別 → 表示に使う絵文字グリフ（"avatar" は画像描画のため対象外）。 */
export const STAMP_KIND_GLYPH: Record<EmojiStampKind, string> = {
  "thumbs-up": "👍",
  heart: "❤️",
  party: "🎉",
  grin: "😄",
  surprised: "😮",
  pray: "🙏",
  eyes: "👀",
  bulb: "💡",
  check: "✅",
  rocket: "🚀",
  fire: "🔥",
  smile: "😊",
  hundred: "💯",
  plus: "➕",
  cry: "😢",
  sob: "😭",
  disappointed: "😞",
  pensive: "😔",
  "broken-heart": "💔",
};

/** スタンプ種別 → アクセシビリティ用の日本語ラベル。 */
export const STAMP_KIND_LABEL: Record<StampKind, string> = {
  "thumbs-up": "いいね",
  heart: "ハート",
  party: "クラッカー",
  grin: "にっこり",
  surprised: "びっくり",
  pray: "お願い",
  eyes: "注目",
  bulb: "ひらめき",
  check: "チェック",
  rocket: "ロケット",
  fire: "炎",
  smile: "笑顔",
  hundred: "100点",
  plus: "プラス",
  cry: "泣き顔",
  sob: "大泣き",
  disappointed: "がっかり",
  pensive: "しょんぼり",
  "broken-heart": "傷心",
  avatar: "アバター",
};

/** 未知の種別を旧いクライアントが受け取った場合に表示する代替グリフ。 */
export const UNKNOWN_STAMP_GLYPH = "❔";

/** 未知の種別を旧いクライアントが受け取った場合のアクセシビリティ用ラベル。 */
export const UNKNOWN_STAMP_LABEL = "不明なスタンプ";
